
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    ArrowLeftIcon, UserGroupIcon, SquaresPlusIcon, CheckCircleIcon,
    ExclamationTriangleIcon, ArrowPathIcon, MagnifyingGlassIcon, LockClosedIcon
} from '@heroicons/react/24/outline';
import './AssignAppsPage.css';

// Reads the single base URL directly from your .env file.
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const AUTH_TOKEN_KEY = 'authToken';
const USER_DETAILS_KEY = 'loggedInUserDetails';

// --- Searchable Dropdown Component ---
const SearchableUserDropdown = ({ users, selectedUserId, onSelect, isLoading, disabled, placeholder }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);

    const selectedUser = useMemo(() => users.find(u => u.id === selectedUserId), [users, selectedUserId]);

    const filteredUsers = useMemo(() => {
        if (!searchTerm) return users;
        return users.filter(user =>
            user.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [users, searchTerm]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelectUser = (user) => {
        onSelect(user.id);
        setIsOpen(false);
        setSearchTerm('');
    };

    return (
        <div className="searchable-dropdown-wrapper" ref={dropdownRef}>
            <button
                type="button"
                className="form-select searchable-trigger"
                onClick={() => setIsOpen(!isOpen)}
                disabled={disabled || isLoading}
            >
                <span className="truncate">
                    {selectedUser ? selectedUser.name : placeholder}
                </span>
                <span className={`arrow-indicator ${isOpen ? 'open' : ''}`}></span>
            </button>
            {isOpen && (
                <div className="searchable-dropdown-panel">
                    <div className="search-input-wrapper">
                        <MagnifyingGlassIcon className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search by name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            autoFocus
                        />
                    </div>
                    <ul className="options-list">
                        {isLoading ? (
                            <li className="option-item-message">Loading users...</li>
                        ) : filteredUsers.length > 0 ? (
                            filteredUsers.map(user => (
                                <li
                                    key={user.id}
                                    className={`option-item ${user.id === selectedUserId ? 'selected' : ''}`}
                                    onClick={() => handleSelectUser(user)}
                                >
                                    {user.name}
                                </li>
                            ))
                        ) : (
                            <li className="option-item-message">No users found.</li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};


const AssignAppsPage = () => {
    const navigate = useNavigate();
    const [currentUser, setCurrentUser] = useState(null);
    const [isLoadingPage, setIsLoadingPage] = useState(true);
    const [error, setError] = useState('');

    const [allUsers, setAllUsers] = useState([]);
    const [availableApps, setAvailableApps] = useState([]);

    const [selectedUserId, setSelectedUserId] = useState('');
    const [selectedAppIds, setSelectedAppIds] = useState([]);
    // New state to track apps the user already has
    const [alreadyAssignedAppIds, setAlreadyAssignedAppIds] = useState([]);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [submitError, setSubmitError] = useState('');

    useEffect(() => {
        const verifyAndFetch = async () => {
            setIsLoadingPage(true);
            setError('');
            const storedUserDetailsString = localStorage.getItem(USER_DETAILS_KEY);
            const token = localStorage.getItem(AUTH_TOKEN_KEY);

            if (!storedUserDetailsString || !token) {
                navigate('/login');
                return;
            }

            try {
                const userDetails = JSON.parse(storedUserDetailsString);
                setCurrentUser(userDetails);

                if (!userDetails.role || !userDetails.role.name || userDetails.role.name.toUpperCase() !== 'SUPER_ADMIN') {
                    navigate('/dashboard');
                    return;
                }

                // 1. Fetch and Filter Users (Active only, sorted by Name)
                const usersResponse = await fetch(`${API_BASE_URL}/api/get-users`, {
                    headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
                });
                if (!usersResponse.ok) throw new Error(`Failed to fetch users: ${usersResponse.status}`);
                const usersData = await usersResponse.json();

                const activeUsers = (usersData.userData || [])
                    .filter(user => user.active === true)
                    .sort((a, b) => a.name.localeCompare(b.name));
                setAllUsers(activeUsers);


                // 2. Fetch and Sort Apps (Sorted by ID)
                const appsResponse = await fetch(`${API_BASE_URL}/api/get-app`, {
                    headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
                });
                if (!appsResponse.ok) throw new Error(`Failed to fetch apps: ${appsResponse.status}`);
                const appsData = await appsResponse.json();

                const rawApps = Array.isArray(appsData) ? appsData : (appsData.applications || appsData.apps || []);

                // Sort apps by appId (numeric sort)
                const sortedApps = [...rawApps].sort((a, b) => {
                    return a.appId - b.appId;
                });

                setAvailableApps(sortedApps);

            } catch (err) {
                console.error("Error in verifyAndFetch:", err);
                setError(err.message || "An error occurred while loading page data.");
            } finally {
                setIsLoadingPage(false);
            }
        };
        verifyAndFetch();
    }, [navigate]);

    // Handle User Selection from Dropdown
    const handleUserSelect = (userId) => {
        setSelectedUserId(userId);
        setSubmitError('');
        setSuccessMessage('');
        setSelectedAppIds([]); // Clear new selections when user changes
        setAlreadyAssignedAppIds([]); // Reset already assigned

        // Logic to determine what apps the user ALREADY has.
        // We look for 'assignedApps', 'apps', or 'appIds' in the user object from 'allUsers'.
        const user = allUsers.find(u => u.id === userId);
        if (user) {
            // Check common properties where backend might store existing apps
            const existingApps = user.assignedApps || user.apps || user.appIds || [];
            
            // Map to IDs (handle cases where it's an array of objects or just IDs)
            const extractedIds = existingApps.map(item => 
                (typeof item === 'object' && item.appId) ? item.appId : item
            );
            
            setAlreadyAssignedAppIds(extractedIds);
        }
    };

    const handleAppSelectionChange = (appId) => {
        // Prevent selecting if it's already assigned (double safety)
        if (alreadyAssignedAppIds.includes(appId)) return;

        setSelectedAppIds(prevSelectedAppIds =>
            prevSelectedAppIds.includes(appId)
                ? prevSelectedAppIds.filter(id => id !== appId)
                : [...prevSelectedAppIds, appId]
        );
        setSubmitError(''); 
        setSuccessMessage('');
    };

    const handleSubmitAssignment = async (e) => {
        e.preventDefault();
        if (!selectedUserId) {
            setSubmitError("Please select a user.");
            return;
        }
        if (selectedAppIds.length === 0) {
            setSubmitError("Please select at least one new app to assign.");
            return;
        }

        setIsSubmitting(true);
        setSuccessMessage('');
        setSubmitError('');
        const token = localStorage.getItem(AUTH_TOKEN_KEY);

        const payload = { userId: selectedUserId, appIds: selectedAppIds };

        try {
            const response = await fetch(`${API_BASE_URL}/api/assign-apps`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                let successMsgText = "Apps assigned successfully!";
                try {
                    const successData = await response.json();
                    successMsgText = successData.message || successMsgText;
                } catch (jsonParseError) { /* Fallback */ }
                
                setSuccessMessage(successMsgText);
                
                // Update local state: Move selected apps to "already assigned" list so UI updates immediately
                setAlreadyAssignedAppIds(prev => [...prev, ...selectedAppIds]);
                setSelectedAppIds([]); // Clear selection
                
            } else {
                let errorMsg = `Failed. Status: ${response.status}`;
                try {
                    const errorData = await response.json();
                    errorMsg = errorData.message || errorData.error || errorMsg;
                } catch (parseError) {
                    const textError = await response.text();
                    errorMsg = textError || errorMsg;
                }
                
                // Specific handling for 400 "apps already assigned" if backend returns it
                if (response.status === 400 && errorMsg.toLowerCase().includes("assigned")) {
                    errorMsg = "One or more selected apps are already assigned to this user.";
                }
                
                throw new Error(errorMsg);
            }
        } catch (err) {
            console.error("Error assigning apps:", err);
            setSubmitError(err.message || "An unknown error occurred.");
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        let timer;
        if (successMessage) timer = setTimeout(() => setSuccessMessage(''), 3000);
        return () => clearTimeout(timer);
    }, [successMessage]);

    useEffect(() => {
        let timer;
        if (submitError) timer = setTimeout(() => setSubmitError(''), 5000);
        return () => clearTimeout(timer);
    }, [submitError]);

    if (isLoadingPage) {
        return (
            <div className="assign-apps-page-loading-container">
                <ArrowPathIcon className="loading-spinner-icon" />
                <p>Loading Admin Portal...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="assign-apps-page error-container">
                <ExclamationTriangleIcon className="error-icon-large" />
                <h2>Error Loading Page</h2>
                <p>{error}</p>
                <Link to="/dashboard" className="button-primary-alt">Back to Dashboard</Link>
            </div>
        );
    }

    return (
        <div className="assign-apps-page-container">
            <header className="assign-apps-header">
                <Link to="/dashboard" className="back-to-dashboard-link">
                    <ArrowLeftIcon className="icon" />
                    <span>Back to Dashboard</span>
                </Link>
                <div className="header-title-group">
                    <h1>Assign Applications</h1>
                    <p>Manage user access by assigning specific applications.</p>
                </div>
            </header>

            <div className="assign-apps-content-area">
                <form onSubmit={handleSubmitAssignment} className="assign-apps-form-card">
                    <div className="form-section user-selection-section">
                        <div className="section-header">
                            <UserGroupIcon className="section-icon" />
                            <h2>Select User</h2>
                        </div>
                        <div className="form-group">
                            <label htmlFor="user-select" className="sr-only">User</label>
                            <SearchableUserDropdown
                                users={allUsers}
                                selectedUserId={selectedUserId}
                                onSelect={handleUserSelect}
                                isLoading={isLoadingPage}
                                disabled={isSubmitting || allUsers.length === 0}
                                placeholder={allUsers.length === 0 && !isLoadingPage ? "No active users available" : "-- Select a User --"}
                            />
                        </div>
                    </div>

                    <div className="form-section app-selection-section">
                        <div className="section-header">
                            <SquaresPlusIcon className="section-icon" />
                            <h2>Select Applications</h2>
                        </div>
                        {availableApps.length === 0 && !isLoadingPage && <p className="info-text-alt">No applications found in the system.</p>}
                        <div className="apps-grid">
                            {availableApps.map(app => {
                                const isAlreadyAssigned = alreadyAssignedAppIds.includes(app.appId);
                                const isSelected = selectedAppIds.includes(app.appId);
                                
                                return (
                                    <label 
                                        key={app.appId} 
                                        htmlFor={`app-${app.appId}`} 
                                        className={`app-tile ${isSelected ? 'selected' : ''} ${isAlreadyAssigned ? 'disabled-tile' : ''}`}
                                        style={isAlreadyAssigned ? { opacity: 0.7, backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                                    >
                                        <input 
                                            type="checkbox" 
                                            id={`app-${app.appId}`} 
                                            value={app.appId} 
                                            checked={isSelected || isAlreadyAssigned} 
                                            onChange={() => handleAppSelectionChange(app.appId)} 
                                            disabled={isSubmitting || !selectedUserId || isAlreadyAssigned} 
                                            className="sr-only" 
                                        />
                                        <div className="app-tile-icon">
                                            {isAlreadyAssigned ? <LockClosedIcon className="generic-app-icon" style={{color: '#6b7280'}} /> : <SquaresPlusIcon className="generic-app-icon" />}
                                        </div>
                                        <span className="app-tile-name">{app.appName}</span>
                                        
                                        {/* Status Indicators */}
                                        {isAlreadyAssigned && (
                                            <div className="status-indicator assigned" style={{ color: '#059669', fontSize: '0.75rem', fontWeight: '600', marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <CheckCircleIcon style={{ width: '16px', height: '16px' }} />
                                                <span>Already Assigned</span>
                                            </div>
                                        )}
                                        {isSelected && !isAlreadyAssigned && <CheckCircleIcon className="selected-checkmark" />}
                                    </label>
                                );
                            })}
                        </div>
                    </div>

                    <div className="form-footer">
                        {successMessage && <div className="form-message-inline success"><CheckCircleIcon className="message-icon" /> {successMessage}</div>}
                        {submitError && <div className="form-message-inline error"><ExclamationTriangleIcon className="message-icon" /> {submitError}</div>}
                        <button type="submit" className="submit-assign-button" disabled={isSubmitting || !selectedUserId || selectedAppIds.length === 0}>
                            {isSubmitting ? (<><ArrowPathIcon className="button-spinner-icon" />Assigning...</>) : 'Assign Selected Apps'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AssignAppsPage;